/**
 * Helper methods to calculate and verify a Luhn algorithm check digit.
 * @see https://en.wikipedia.org/wiki/Luhn_algorithm
 */

/**
 * Generates the check digit for a number using the Luhn algorithm:
 * https://en.wikipedia.org/wiki/Luhn_algorithm
 *
 * The steps to calculate are:
 * 1. Start from the rightmost digit. Moving left, double the value of every second digit (including the rightmost digit).
 * 2. If that value is greater than 9, subtract 9 from it.
 * 3. Sum all of the digits together
 * 4. Multiply the sum by 9 and the check digit will be that value modulo 10.
 *
 * @param number the number for which to get the Luhn's check digit
 * @return the check digit for the given number
 */
export const calculateLuhnCheckDigit = (number) => {
	if (!Number.isInteger(number)) {
		throw Error(`Cannot calculate Luhn check digit on input '${number}'`);
	}
	let sum = 0;
	let multiply = true;
	const digits = number + '';

	for (let i = digits.length - 1; i >= 0; i--) {
		let digit = Number(digits.charAt(i)); // get the digit at the given index
		if (multiply) {
			// double every other digit
			digit = digit * 2;
		}
		if (digit > 9) {
			// subtract 9 if the value is greater than 9
			digit -= 9;
		}
		sum += digit; // add the digit to the sum

		multiply = !multiply; // Flip the multiply flag for the next digit
	}

	return (sum * 9) % 10;
};

/**
 * Verify the numeric portion of the given string ends in a valid Luhn check digit.
 *
 * 1. Remove any non-numeric characters from the string.
 * 2. Remove the last digit from the string. This is the original check digit to validate.
 * 3. Calculate the Luhn check digit for the remaining portion of the string.
 * 4. Compare the calculated check digit with the original check digit.
 *
 * This operation is null-safe. A string with less than 2 numeric digits will return false.
 *
 * @param input The string to validate
 * @return true if the string ends with a valid Luhn check digit, false if the original check 
 *         digit does not match or if the string is not valid for verification
 */
export const verifyLuhnsCheckDigit = (input) => {
	if (!input) {
		return false;
	}

	const numericInput = input.replaceAll(/[^0-9]/g, '');
	if (numericInput.length < 2) {
		return false;
	}

	const lastDigit = Number(numericInput.substring(numericInput.length - 1));
	const payload = Number(numericInput.substring(0, numericInput.length - 1));
	const checkSum = calculateLuhnCheckDigit(payload);

	return lastDigit === checkSum;
};
